import ApiClient from '@/services/apiClient';
import i18n from '@/i18n';

const t = (key, params) => i18n.global.t(key, params);

// Brands handled by the video integration (camera_brand in the installer API)
export const BRAND_DAHUA = 'DAHUA';
export const BRAND_HIK = 'HIK';
export const BRAND_ONVIF = 'ONVIF';
export const VIDEO_BRANDS = [BRAND_DAHUA, BRAND_HIK, BRAND_ONVIF];

// Keys read or computed by the site. As in the app, the other keys returned by the API
// (enabled, codec_compat...) are kept in extras and sent back unchanged on update.
const KNOWN_KEYS = [
    'id', 'hub_id', 'online', 'status_at',
    'name', 'ip', 'brand', 'username', 'password', 'port', 'http_port',
    'nvr_id', 'channel', 'rtsp_path', 'rtsp_path_main', 'ptz_url', 'ptz', 'vod', 'readers', 'vod_pre_roll_secs', 'vod_duration_secs',
];

// The API never returns the password: an empty password is not sent and the stored one is kept
export class VideoDevice {
    id = 0;
    name = '';
    ip = '';
    brand = BRAND_ONVIF;
    username = '';
    password = '';
    port = null;        // RTSP port, 554 when null
    http_port = null;   // brand API / ONVIF port, 80 when null
    online = null;      // null until the LINK reports the device
    extras = {};

    // Devices the app cannot read (no id or no name) are skipped, as in the app
    static isReadable(data) {
        return data != null && typeof data === 'object' && Number.isInteger(data['id']) && typeof data['name'] === 'string';
    }

    static readCommon(obj, data) {
        obj.id = data['id'];
        obj.name = data['name'];
        obj.ip = ApiClient.convertToType(data['ip'], 'String') ?? '';
        obj.brand = ApiClient.convertToType(data['brand'], 'String') ?? BRAND_ONVIF;
        obj.username = ApiClient.convertToType(data['username'], 'String') ?? '';
        obj.password = '';
        obj.port = ApiClient.convertToType(data['port'], 'Integer') ?? null;
        obj.http_port = ApiClient.convertToType(data['http_port'], 'Integer') ?? null;
        obj.online = ApiClient.convertToType(data['online'], 'Boolean') ?? null;
        obj.extras = Object.fromEntries(Object.entries(data).filter(([key]) => !KNOWN_KEYS.includes(key)));
        return obj;
    }

    clone() {
        const copy = Object.assign(new this.constructor(), this);
        copy.extras = { ...this.extras };
        return copy;
    }

    // True when the brand can be changed (DAHUA / HIK), ONVIF devices keep their brand
    get hasBrandChoice() {
        return this.brand !== BRAND_ONVIF;
    }

    get isOnvif() {
        return this.brand === BRAND_ONVIF;
    }

    get isNew() {
        return this.id === 0;
    }

    get key() {
        return `${this.type}_${this.id}`;
    }

    get isConnecting() {
        return this.online == null;
    }

    get isActive() {
        return this.online === true;
    }

    get stateLabel() {
        if (this.online == null) return t('video.connecting');
        return this.online ? t('common.active') : t('common.inactive');
    }

    toJSON() {
        const json = { ...this.extras };
        json.name = this.name;
        json.brand = this.brand;
        json.ip = this.ip;
        json.username = this.username;
        if (this.password) json.password = this.password;
        if (this.port != null) json.port = this.port;
        if (this.http_port != null) json.http_port = this.http_port;
        return json;
    }

    // Body of POST .../cameras/probe: the probe reaches the device on its HTTP port
    toProbeJSON() {
        const json = {
            ip: this.ip,
            username: this.username,
            password: this.password,
            brand: this.brand,
        };
        if (this.http_port != null) json.port = this.http_port;
        return json;
    }
}

export class NVR extends VideoDevice {
    static constructFromObject(data) {
        return VideoDevice.readCommon(new NVR(), data);
    }

    static create(brand) {
        const nvr = new NVR();
        nvr.name = t('video.newNvr');
        nvr.brand = brand;
        return nvr;
    }

    get type() {
        return 'nvr';
    }

    get apiPath() {
        return 'nvrs';
    }

    get icon() {
        return 'mdi-harddisk';
    }

    get pageTitle() {
        return this.isNew ? t('video.newNvr') : t('video.editNvr');
    }

    get deleteConfirm() {
        return t('video.nvrDeleteConfirm');
    }

    get addedMessage() {
        return t('video.nvrAdded');
    }

    get typeLabel() {
        return `NVR ${this.brand}`;
    }
}

export class Camera extends VideoDevice {
    nvr_id = null;              // the camera is a channel of this NVR, null for a standalone camera
    channel = 1;                // NVR channel, 1 for a standalone camera
    rtsp_path = '';             // sub stream, required for the ONVIF brand
    rtsp_path_main = '';        // main stream override
    ptz_url = '';
    ptz = false;
    vod = false;                // video on demand
    readers = [];               // access readers watched by the camera
    vod_pre_roll_secs = 10;
    vod_duration_secs = 30;

    static constructFromObject(data) {
        const obj = VideoDevice.readCommon(new Camera(), data);
        obj.nvr_id = ApiClient.convertToType(data['nvr_id'], 'Integer') ?? null;
        obj.channel = ApiClient.convertToType(data['channel'], 'Integer') ?? 1;
        obj.rtsp_path = ApiClient.convertToType(data['rtsp_path'], 'String') ?? '';
        obj.rtsp_path_main = ApiClient.convertToType(data['rtsp_path_main'], 'String') ?? '';
        obj.ptz_url = ApiClient.convertToType(data['ptz_url'], 'String') ?? '';
        obj.ptz = ApiClient.convertToType(data['ptz'], 'Boolean') ?? false;
        obj.vod = ApiClient.convertToType(data['vod'], 'Boolean') ?? false;
        obj.readers = Array.isArray(data['readers']) ? data['readers'].map(r => CameraReader.constructFromObject(r)).filter(Boolean) : [];
        obj.vod_pre_roll_secs = ApiClient.convertToType(data['vod_pre_roll_secs'], 'Integer') ?? 10;
        obj.vod_duration_secs = ApiClient.convertToType(data['vod_duration_secs'], 'Integer') ?? 30;
        return obj;
    }

    static create(brand) {
        const camera = new Camera();
        camera.name = t('video.newCamera');
        camera.brand = brand;
        return camera;
    }

    // Camera declared from a channel of a probed NVR: inherits the NVR's brand, address and credentials
    static fromProbedChannel(nvr, probed) {
        const defaultName = t('video.channelLabel', { channel: probed.channel });
        const name = probed.name ? probed.name : `${nvr.name} - ${defaultName}`;

        const camera = new Camera();
        camera.name = name.substring(0, 32);
        camera.brand = nvr.brand;
        camera.nvr_id = nvr.id;
        camera.channel = probed.channel;
        camera.rtsp_path = probed.subStream?.rtsp_path ?? '';
        camera.rtsp_path_main = probed.mainStream?.rtsp_path ?? '';
        camera.ptz = probed.ptz ?? false;
        camera.vod = probed.recorded ?? false;   // playback only makes sense on a recorded channel
        return camera;
    }

    clone() {
        const copy = super.clone();
        copy.readers = this.readers.map(r => new CameraReader(r.hub_id, r.position));
        return copy;
    }

    get isOnNvr() {
        return this.nvr_id != null;
    }

    get type() {
        return 'camera';
    }

    get apiPath() {
        return 'cameras';
    }

    get icon() {
        return 'mdi-video';
    }

    get pageTitle() {
        return this.isNew ? t('video.newCamera') : t('video.editCamera');
    }

    get deleteConfirm() {
        return t('video.cameraDeleteConfirm');
    }

    get addedMessage() {
        return t('video.cameraAdded');
    }

    get typeLabel() {
        return this.isOnNvr ? t('video.cameraOnNvr') : t('video.cameraType', { brand: this.brand });
    }

    toJSON() {
        let json;
        if (this.isOnNvr) {
            // A camera attached to an NVR inherits its brand, address and credentials: those fields must be absent
            json = { ...this.extras };
            json.name = this.name;
            json.nvr_id = this.nvr_id;
            if (this.port != null) json.port = this.port;
            if (this.http_port != null) json.http_port = this.http_port;
        } else {
            json = super.toJSON();
        }

        json.channel = this.channel;
        if (this.rtsp_path) json.rtsp_path = this.rtsp_path;
        if (this.rtsp_path_main) json.rtsp_path_main = this.rtsp_path_main;
        if (this.ptz_url) json.ptz_url = this.ptz_url;
        json.ptz = this.ptz;
        json.vod = this.vod;
        // Replaced wholesale by the API: always sent, empty unlinks every reader
        json.readers = this.vod ? this.readers.map(r => r.toJSON()) : [];
        json.vod_pre_roll_secs = this.vod_pre_roll_secs;
        json.vod_duration_secs = this.vod_duration_secs;
        return json;
    }
}

// Access reader linked to a camera, identified by its hub and its position
export class CameraReader {
    constructor(hub_id, position) {
        this.hub_id = hub_id;
        this.position = position;
    }

    static constructFromObject(data) {
        if (!data || !Number.isInteger(data['hub_id']) || !Number.isInteger(data['position'])) return null;
        return new CameraReader(data['hub_id'], data['position']);
    }

    get key() {
        return `${this.hub_id}_${this.position}`;
    }

    equals(other) {
        return other.hub_id === this.hub_id && other.position === this.position;
    }

    toJSON() {
        return { hub_id: this.hub_id, position: this.position };
    }
}

// Device answering the ONVIF WS-Discovery probe run by the LINK
export class DiscoveredCamera {
    static constructFromObject(data) {
        if (!data || typeof data['ip'] !== 'string') return null;
        const obj = new DiscoveredCamera();
        obj.ip = data['ip'];
        obj.xaddrs = Array.isArray(data['xaddrs']) ? data['xaddrs'] : [];
        obj.name = data['name'] ?? null;
        obj.hardware = data['hardware'] ?? null;
        obj.location = data['location'] ?? null;
        obj.brand = data['brand'] ?? null;
        obj.endpoint = data['endpoint'] ?? null;
        return obj;
    }

    // Name, else hardware model, else IP
    get label() {
        return this.name || this.hardware || this.ip;
    }

    // IP with model and brand when known
    get details() {
        const parts = [this.ip];
        if (this.hardware && this.hardware !== this.label) parts.push(this.hardware);
        if (this.brand) parts.push(this.brand);
        return parts.join(' - ');
    }
}

// Result of POST .../cameras/probe: identity of the device and its channels with their streams
export class ProbeResult {
    static constructFromObject(data) {
        const obj = new ProbeResult();
        data = data || {};
        obj.is_nvr = data['is_nvr'] ?? false;
        obj.http_base = data['http_base'] ?? null;
        obj.manufacturer = data['manufacturer'] ?? null;
        obj.model = data['model'] ?? null;
        obj.firmware = data['firmware'] ?? null;
        obj.serial = data['serial'] ?? null;
        obj.channels = Array.isArray(data['channels']) ? data['channels'].map(c => ProbedChannel.constructFromObject(c)).filter(Boolean) : [];
        return obj;
    }

    // HTTP port the probe reached the device on, null when unknown or default
    get http_port() {
        if (!this.http_base) return null;
        try {
            const port = new URL(this.http_base).port;
            return port ? parseInt(port, 10) : null;
        } catch (error) {
            return null;
        }
    }
}

export class ProbedChannel {
    static constructFromObject(data) {
        if (!data || !Number.isInteger(data['channel'])) return null;
        const obj = new ProbedChannel();
        obj.channel = data['channel'];
        obj.name = data['name'] ?? null;
        obj.online = data['online'] ?? null;
        obj.ptz = data['ptz'] ?? null;
        obj.recorded = data['recorded'] ?? null;
        obj.streams = Array.isArray(data['streams']) ? data['streams'].map(s => ProbedStream.constructFromObject(s)).filter(Boolean) : [];
        return obj;
    }

    get subStream() {
        return this.streams.find(s => s.role === 'sub') ?? null;
    }

    get mainStream() {
        return this.streams.find(s => s.role === 'main') ?? null;
    }

    // Channel name, else "Channel N"
    get label() {
        return this.name || t('video.channelLabel', { channel: this.channel });
    }
}

export class ProbedStream {
    static constructFromObject(data) {
        if (!data || typeof data !== 'object') return null;
        const obj = new ProbedStream();
        obj.role = data['role'] ?? null;     // main / sub
        obj.encoding = data['encoding'] ?? null;
        obj.width = data['width'] ?? null;
        obj.height = data['height'] ?? null;
        obj.rtsp_path = data['rtsp_path'] ?? null;
        return obj;
    }

    // Encoding and resolution when known
    get description() {
        const parts = [];
        if (this.encoding) parts.push(this.encoding);
        if (this.width != null && this.height != null) parts.push(`${this.width}x${this.height}`);
        return parts.join(' ');
    }
}
